import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useFetch } from "hooks";
import { post } from "utils/api";
import { Formik, Form } from "formik";
import { InlineWaiting } from "components/ui";
import FormikField from "components/forms/formik-field";
import * as Yup from "yup";
import { isValidPhone } from "utils/redux-form";
import PhoneNumberField from "components/forms/phone-number-field";
import AuthCodeInput from "components/two-factor-auth/auth-code-input";
import { MdVerifiedUser } from "react-icons/lib/md";
import { formatPhone } from "utils/misc";
import FaEraser from "react-icons/lib/fa/eraser";

// The server answers a refused change with { status: "<message for the user>" } and a
// throttled one with a bare 429.
const mfaErrorText = error =>
  error?.status === 429
    ? "Too many attempts. Please wait a minute and try again."
    : error?.response?.body?.status ??
      "Sorry something went wrong, this request could not be processed";

const MFAPhoneNumber = ({ user }) => {
  return (
    <div style={{ padding: 10, marginTop: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          fontSize: "1.05rem",
          fontWeight: "400",
          padding: "5px"
        }}
      >
        <h4>Cell Phone #</h4>
        {user.MFACellNumber && user.MFACellNumberVerified ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <ResetMFAPhoneNumber />
            <span>{formatPhone(user.MFACellNumber)}</span>
            <span style={{ color: "green", paddingLeft: 5 }}>
              <span title="Verified">
                <MdVerifiedUser />
              </span>
            </span>
          </div>
        ) : (
          <EditMFAPhoneNumber user={user} />
        )}
      </div>
    </div>
  );
};

MFAPhoneNumber.propTypes = { user: PropTypes.object };

export default MFAPhoneNumber;

// Removing the MFA phone needs the current password (S3DEV2-7665). POST, not del(): del()
// sends params as a query string, which would put the password in the URL.
const ResetMFAPhoneNumber = () => {
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const dispatch = useDispatch();
  const resetMFAPhoneNumberApi = useFetch({
    apiFn: password => post("mfa-cell-reset", { password })
  });

  if (!isConfirming)
    return (
      <div>
        <span
          className="clear-form"
          style={{
            margin: "5px 5px",
            paddingRight: 30,
            color: "red",
            cursor: "pointer",
            fontSize: "0.9rem",
            transition: "all 0.2s ease-in-out"
          }}
          onClick={() => {
            if (
              confirm(
                "Are you sure you want to remove this verified number? You will no longer be able to recieve two factor auth codes via sms."
              )
            ) {
              setIsConfirming(true);
            }
          }}
        >
          <FaEraser /> Reset MFA Cell Number
        </span>
      </div>
    );

  return (
    <form
      style={{ display: "flex", flexDirection: "column", paddingRight: 30 }}
      onSubmit={async e => {
        e.preventDefault();
        // The submit button is hidden while fetching, but Enter in the password field still
        // submits a form with a single input.
        if (resetMFAPhoneNumberApi.isFetching) return;
        const result = await resetMFAPhoneNumberApi.fetchData(password);
        if (result == true) {
          dispatch({
            type: "USER_SETTINGS_UPDATE_EVENT",
            payload: {
              MFACellNumber: "",
              MFACellNumberVerified: false
            }
          });
        }
      }}
    >
      <label>
        Enter your current password to remove this number
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
      </label>
      <div style={{ display: "flex", gap: 10 }}>
        {resetMFAPhoneNumberApi.isFetching ? (
          <InlineWaiting />
        ) : (
          <>
            <button
              type="submit"
              disabled={!password}
              style={{ padding: "10px 10px", margin: 0 }}
            >
              Remove
            </button>
            <button
              type="button"
              className="button secondary"
              style={{ padding: "10px 10px", margin: 0 }}
              onClick={() => {
                setPassword("");
                setIsConfirming(false);
              }}
            >
              Cancel
            </button>
          </>
        )}
      </div>
      <div style={{ color: "red" }}>
        {resetMFAPhoneNumberApi.error &&
          mfaErrorText(resetMFAPhoneNumberApi.error)}
      </div>
    </form>
  );
};

const EditMFAPhoneNumber = () => {
  const [editingStep, setEditingStep] = React.useState(0);
  const [phoneNumber, setPhoneNumber] = React.useState("");

  const [phoneNumberVerifyCode, setPhoneNumberVerifyCode] = React.useState("");
  const [restartMessage, setRestartMessage] = React.useState("");
  const dispatch = useDispatch();
  const verifyPhoneNumberApi = useFetch({
    apiFn: ({ phone_number, code }) =>
      post(`mfa-cell-verify`, { phone_number, code }),
    // The server sets restart once no code can succeed (too many wrong codes, expired or
    // already used), so send the user back to request a new one instead of guessing on.
    onError: error => {
      if (error?.response?.body?.restart) {
        setRestartMessage(mfaErrorText(error));
        setPhoneNumberVerifyCode("");
        setEditingStep(0);
      }
    }
  });

  const setPhoneNumberApi = useFetch({
    apiFn: ({ phone_number, password }) =>
      post(`mfa-cell-set`, { phone_number, password })
  });
  if (editingStep == 0)
    return (
      <div>
        For security purposes we have enacted an extra level of validation for
        all users with access to private patient data.
        <div>
          Our records indicate we do not have verified cell phone number on file
          for you
        </div>
        <div>
          Please verify your phone number to allow this to be used for verifing
          your identity
        </div>
        <Formik
          initialValues={{ phone_number: "", password: "" }}
          validationSchema={Yup.object({
            phone_number: Yup.string()
              .required("Required")
              .test(
                "valid-number",
                "Invalid phone number",
                val => !isValidPhone(val)
              ),
            password: Yup.string().required("Required")
          })}
          onSubmit={async ({ phone_number, password }) => {
            setRestartMessage("");
            const result = await setPhoneNumberApi.fetchData({
              phone_number,
              password
            });
            if (result == true) {
              setPhoneNumber(phone_number);
              setEditingStep(1);
            }
          }}
        >
          {({ isValid, isSubmitting }) => (
            <Form
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto",
                gridGap: 10,
                alignItems: "center"
              }}
            >
              <FormikField
                name="phone_number"
                label="Please Enter cell phone number"
                type="tel"
                component={PhoneNumberField}
              />
              <FormikField
                name="password"
                label="Current password"
                type="password"
                autoComplete="current-password"
              />
              <div>
                {isSubmitting ? (
                  <InlineWaiting />
                ) : (
                  <button
                    disabled={!isValid}
                    type="submit"
                    style={{ padding: "10px 10px", margin: 0 }}
                  >
                    Submit
                  </button>
                )}
              </div>
            </Form>
          )}
        </Formik>
        <div style={{ color: "red" }}>
          {setPhoneNumberApi.error
            ? mfaErrorText(setPhoneNumberApi.error)
            : restartMessage}
        </div>
      </div>
    );
  if (editingStep == 1)
    return (
      <div>
        <label>Please enter code to verify this phone number</label>
        <AuthCodeInput
          value={phoneNumberVerifyCode}
          onChange={value => {
            setPhoneNumberVerifyCode(value);
          }}
        />
        <button
          disabled={phoneNumberVerifyCode.length != 6}
          onClick={async () => {
            const result = await verifyPhoneNumberApi.fetchData({
              phone_number: phoneNumber,
              code: phoneNumberVerifyCode
            });
            if (result == true) {
              dispatch({
                type: "USER_SETTINGS_UPDATE_EVENT",
                payload: {
                  MFACellNumber: phoneNumber,
                  MFACellNumberVerified: true
                }
              });
              setEditingStep(2);
            }
          }}
        >
          Submit
        </button>
        <div style={{ color: "red" }}>
          {/* A restart error is shown on step 0 via restartMessage. useFetch keeps it until
              the next verify, so skip it here or it greets the user's fresh code input. */}
          {verifyPhoneNumberApi.error &&
            !verifyPhoneNumberApi.error.response?.body?.restart &&
            mfaErrorText(verifyPhoneNumberApi.error)}
        </div>
      </div>
    );
  if (editingStep == 2)
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div>Thank you, cell phone number has been verified</div>
        <div style={{ display: "flex" }}>
          <span>{phoneNumber}</span>
        </div>
      </div>
    );
};
